/**
 * Functional core for app-wide startup + build state.
 *
 * Pure deciders only: each function takes plain data and returns a fresh value
 * or a boolean decision. No `vue`, no `ref`, no IO, no input mutation — the
 * imperative shell (`composables/`) turns these decisions into reactive writes
 * and side effects.
 */

export enum StartupState {
  /** Not yet started */
  NotStarted = 'NotStarted',
  /** Loading data needed for startup (pre-startup) */
  Initializing = 'Initializing',
  /** All data required for startup of application have been loaded from cache and updated from backend if needed */
  ReadyForStartup = 'ReadyForStartup',
  /** Something went wrong with the startup-process */
  Error = 'Error'
}

/** True when `state` is already present in the tracked startup states. */
export function checkStartupStateExists(
  states: readonly StartupState[],
  state: StartupState
): boolean {
  return states.includes(state)
}

/** True when the startup-readiness condition is met. */
export function determineAppReady(states: readonly StartupState[]): boolean {
  return states.includes(StartupState.ReadyForStartup)
}

/**
 * Decide the build-banner display string. Returns `'In development'` in dev or
 * when no build date is available; otherwise the formatted build date.
 *
 * `formatDateTime` is injected so the decision stays pure — the shell supplies
 * the locale-aware formatter and the environment-sourced build date.
 */
export function formatBuildString(
  buildDate: string | undefined,
  isDev: boolean,
  formatDateTime: (date: string) => string
): string {
  if (isDev || !buildDate) {
    return 'In development'
  }
  return formatDateTime(buildDate)
}
